/**
 * CourseModuleRegistry v1 plugins (F2-A): `lesson` (wraps the existing
 * learning-blocks lesson) and `resource` (file/link/text viewer).
 *
 * They are FACTORIES: the app injects its lazy (Solid) components when
 * instantiating; the plugin's core (manifest, schemas, contributions) lives
 * here and is pure and testable. F3 kinds (assignment/forum/video/live_session)
 * are added by registering new plugins — without touching this file or the core.
 */

import type { FeatureManifest } from "../country/packs";
import {
  anyObjectValidator,
  type CourseModulePlugin,
  isPlainObject,
  type ModuleCompletionCtx,
  type ModuleGradeCtx,
  type ModuleValidationResult,
  type SchemaValidator,
} from "./types";

/** Component loaders the app injects (both optional). */
export interface ModuleComponentLoaders<TComponent> {
  authoringComponent?: () => Promise<TComponent>;
  playerComponent?: () => Promise<TComponent>;
}

// ─── lesson ──────────────────────────────────────────────────────────────────

/** Lesson module config: no own fields in v1 (the detail is `lesson`). */
export type LessonModuleConfig = Record<string, unknown>;

const LESSON_MANIFEST: FeatureManifest = {
  key: "academy.module.lesson",
  name: "Lección interactiva",
  description:
    "Lección de bloques de aprendizaje (quiz, flashcards, código…) con corrección server-authoritative y XP.",
  scope: "course",
  status: "stable",
};

/** A lesson's completion contribution: 1 if completed, 0 if not. */
export function lessonCompletionContribution(ctx: ModuleCompletionCtx): number | null {
  if (!ctx.module.ref_id) {
    return null;
  }
  return ctx.lessonProgress?.status === "completed" ? 1 : 0;
}

/** A lesson's grade contribution: its authoritative score (0..1). */
export function lessonGradeContribution(ctx: ModuleGradeCtx): number | null {
  if (!ctx.module.ref_id) {
    return null;
  }
  const score = ctx.lessonProgress?.score;
  return typeof score === "number" ? Math.max(0, Math.min(1, score)) : 0;
}

export function createLessonModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "lesson",
    manifest: LESSON_MANIFEST,
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    completionContribution: lessonCompletionContribution,
    gradeContribution: lessonGradeContribution,
  };
}

// ─── resource ────────────────────────────────────────────────────────────────

export type ResourceKind = "file" | "link" | "text";

/** Resource module config (mirror of the `resource` row for validation). */
export interface ResourceModuleConfig extends Record<string, unknown> {
  kind: ResourceKind;
  title?: string;
  url?: string;
  storage_key?: string;
  body?: string;
}

const RESOURCE_MANIFEST: FeatureManifest = {
  key: "academy.module.resource",
  name: "Recurso",
  description: "Material de apoyo del curso: archivo descargable, enlace externo o texto.",
  scope: "course",
  status: "stable",
};

export const RESOURCE_KINDS: readonly ResourceKind[] = ["file", "link", "text"];

/** Type guard for the resource kind (no unsafe assertions). */
export function isResourceKind(kind: string): kind is ResourceKind {
  return kind === "file" || kind === "link" || kind === "text";
}

/** Validates a resource's config: known kind + the field that kind requires. */
export function validateResourceConfig(
  value: unknown,
): ModuleValidationResult<ResourceModuleConfig> {
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["resource config must be an object"] };
  }
  const { kind } = value;
  if (typeof kind !== "string" || !isResourceKind(kind)) {
    return { ok: false, errors: [`resource kind must be one of ${RESOURCE_KINDS.join("|")}`] };
  }
  const errors: string[] = [];
  if (kind === "link" && (typeof value.url !== "string" || value.url.trim() === "")) {
    errors.push("link resource requires a non-empty url");
  }
  if (kind === "file" && typeof value.storage_key !== "string" && typeof value.url !== "string") {
    errors.push("file resource requires storage_key or url");
  }
  if (kind === "text" && typeof value.body !== "string") {
    errors.push("text resource requires body");
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  const cfg: ResourceModuleConfig = { ...value, kind };
  return { ok: true, value: cfg };
}

const resourceConfigSchema: SchemaValidator<ResourceModuleConfig> = {
  parse: validateResourceConfig,
};

export function createResourceModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent, ResourceModuleConfig> {
  return {
    kind: "resource",
    manifest: RESOURCE_MANIFEST,
    configSchema: resourceConfigSchema,
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // A resource contributes neither to completion nor grade in v1.
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

// ─── live_session (F3-A · thin) ──────────────────────────────────────────────
//
// THIN kind: the player only shows the linked `class_session` info
// (date/time/room + a "join later" placeholder). The JOIN screen for the
// live class is owned by F3-G; this plugin stays minimal and marked beta.
// The link to the session is via `course_module.ref_id` → `class_session.id`.

/** live_session module config (all optional; the detail lives in the session). */
export interface LiveSessionModuleConfig extends Record<string, unknown> {
  /** id of the linked class_session (redundant with course_module.ref_id). */
  session_id?: string;
  /** Live class provider (jitsi|zoom|meet|custom); informational. */
  provider?: string;
}

const LIVE_SESSION_MANIFEST: FeatureManifest = {
  key: "academy.module.live_session",
  name: "Clase en vivo",
  description:
    "Sesión en vivo enlazada a una clase del horario (fecha/hora/aula), con acceso a la videollamada del proveedor configurado.",
  scope: "course",
  status: "beta",
  // FLAGS: joining a live class needs AT LEAST ONE meeting provider enabled
  // (the join resolves the provider by session→cohort→course).
  requires: [
    "media.provider.jitsi|media.provider.zoom|media.provider.meet|media.provider.custom_link",
  ],
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Paradas «Clase en vivo» en Aprender", "«Clase en vivo» en el menú de módulos"],
    flowsBlocked: ["Unirse a la videollamada", "Configurar el enlace de reunión de una sesión"],
    dataPolicy: "preserved",
  },
};

/** Validates live_session config: permissive object with optional fields. */
export function validateLiveSessionConfig(
  value: unknown,
): ModuleValidationResult<LiveSessionModuleConfig> {
  if (value === null || value === undefined) {
    return { ok: true, value: {} };
  }
  if (!isPlainObject(value)) {
    return { ok: false, errors: ["live_session config must be an object"] };
  }
  return { ok: true, value: value as LiveSessionModuleConfig };
}

export function createLiveSessionModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "live_session",
    manifest: LIVE_SESSION_MANIFEST,
    configSchema: { parse: validateLiveSessionConfig },
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // A live class doesn't contribute to completion or grade by itself (v1).
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

// ─── forum (F3-C) ──────────────────────────────────────────────────────────

/** Forum module config: no own fields in v1 (the detail is `forum`). */
export type ForumModuleConfig = Record<string, unknown>;

const FORUM_MANIFEST: FeatureManifest = {
  key: "academy.module.forum",
  name: "Foro",
  description:
    "Foro de discusión de la cohorte: hilos y respuestas (anidadas 1 nivel) con moderación (fijar/cerrar hilos, ocultar posts).",
  scope: "course",
  status: "stable",
  gateable: true,
  since: "2026-06",
  docsSlug: "profesor",
  implications: {
    navHidden: ["Pestaña Foro de la cohorte", "Paradas «Foro» en Aprender"],
    flowsBlocked: ["Crear hilos nuevos", "Publicar respuestas"],
    dataPolicy: "preserved",
  },
};

export function createForumModulePlugin<TComponent = unknown>(
  components: ModuleComponentLoaders<TComponent> = {},
): CourseModulePlugin<TComponent> {
  return {
    kind: "forum",
    manifest: FORUM_MANIFEST,
    configSchema: anyObjectValidator(),
    authoringComponent: components.authoringComponent,
    playerComponent: components.playerComponent,
    // A forum doesn't contribute to the course's completion or grade.
    completionContribution: () => null,
    gradeContribution: () => null,
  };
}

/** Manifest of the `forum` module kind (for the `pnpm gen:features` bridge). */
export function forumModuleManifest(): FeatureManifest {
  return FORUM_MANIFEST;
}

/** Default registry (no components — for tests and server-side uses). */
export function defaultModuleManifests(): FeatureManifest[] {
  return [LESSON_MANIFEST, RESOURCE_MANIFEST, LIVE_SESSION_MANIFEST, FORUM_MANIFEST];
}

/** Manifests of the module kinds (self-describing catalog → gen:features). */
export function courseModuleManifests(): FeatureManifest[] {
  return defaultModuleManifests();
}
